import React, { useEffect, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";
import {
  declineStudioAccessRequest,
  listStudioAccessRequests,
  shareStudioAccessRequest,
  startStudioAccessTransfer,
} from "../../services/studioCollectorAccessApi";
import StudioOwnershipClaims from "./StudioOwnershipClaims";

type RequestRow = {
  id: string;
  status: string;
  productTitle?: string;
  productPhotoUrl?: string;
  collectorDisplayName?: string;
  collectorPhoneMasked?: string;
  createdAt?: string;
};

const StudioAccessRequests: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<RequestRow[]>([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = () => {
    setError("");
    return listStudioAccessRequests()
      .then((res: any) => {
        if (res?.success === false) {
          throw new Error(res?.message || "Could not load requests");
        }
        setItems(res?.data?.items || []);
      })
      .catch((e: any) => setError(e?.message || "Could not load requests"));
  };

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, []);

  const act = async (id: string, fn: (id: string) => Promise<any>) => {
    setBusyId(id);
    setError("");
    try {
      const res = await fn(id);
      if (res?.success === false) {
        throw new Error(res?.message || "Action failed");
      }
      await load();
    } catch (e: any) {
      setError(e?.message || "Action failed");
    } finally {
      setBusyId("");
    }
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner /></div>;
  }

  return (
    <div className="p-3">
      <h1 className="h4 mb-3">Collector requests</h1>
      <StudioOwnershipClaims />
      <h2 className="h5 mb-2">Detail requests</h2>
      <p className="text-muted small">
        Collectors can ask to see certificate details after the 14-day scan window.
        Share details without transferring ownership, or decline. Ownership transfer ships in a later phase.
      </p>
      {error && <Alert color="danger">{error}</Alert>}
      {items.length === 0 ? (
        <p className="text-muted">No requests yet.</p>
      ) : (
        <ul className="list-unstyled">
          {items.map((row) => (
            <li key={row.id} className="border rounded p-3 mb-3 d-flex gap-3 align-items-start">
              {row.productPhotoUrl && (
                <img src={row.productPhotoUrl} alt="" width={72} height={72} style={{ objectFit: "cover", borderRadius: 8 }} />
              )}
              <div className="flex-grow-1">
                <div className="fw-semibold">{row.productTitle || "Product"}</div>
                <small className="text-muted d-block">
                  {row.collectorDisplayName?.trim() ? row.collectorDisplayName : "Collector"}
                  {row.collectorPhoneMasked ? ` · ${row.collectorPhoneMasked}` : ""}
                  {row.createdAt ? ` · ${new Date(row.createdAt).toLocaleString()}` : ""}
                </small>
                <span className="badge bg-secondary mt-1">{row.status}</span>
                {(row.status === "PENDING" || row.status === "SHARED") && (
                  <div className="mt-2 d-flex flex-wrap gap-2">
                    {row.status === "PENDING" && (
                      <Button size="sm" color="dark" disabled={busyId === row.id} onClick={() => act(row.id, shareStudioAccessRequest)}>
                        Share details
                      </Button>
                    )}
                    <Button size="sm" outline color="secondary" disabled={busyId === row.id} onClick={() => act(row.id, declineStudioAccessRequest)}>
                      {row.status === "SHARED" ? "Revoke" : "Decline"}
                    </Button>
                    <Button
                      size="sm"
                      outline
                      color="dark"
                      disabled={busyId === row.id}
                      onClick={() => act(row.id, startStudioAccessTransfer)}
                    >
                      Start transfer
                    </Button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default StudioAccessRequests;
